import React, { useState } from "react";
import { Button } from "./ui/button";
import { ArrowUpRight } from "lucide-react";
import { TiltCard } from "./TiltCard";
import { RevealText } from "./RevealText";
import { Magnetic } from "./Magnetic";
import { ProjectModal } from "./ProjectModal";

const projects = [
  {
    tag: "E-COMMERCE",
    title: "Fleurish Mjk",
    summary:
      "A boutique florist storefront with seamless checkout, custom bouquet builder, and same-day-delivery booking.",
    image:
      "https://images.pexels.com/photos/14656012/pexels-photo-14656012.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    accent: "#FF6BB5",
    code: ["import { Florist } from '@flowbase/ui'", "// 32% conversion lift"],
    problem:
      "A family-run florist was losing weekend orders to a clunky template store with no bouquet customisation, no delivery scheduling, and a 7-step checkout that bled cart abandons.",
    solution:
      "We rebuilt the storefront on a custom React + headless commerce stack, designed a tactile bouquet builder, and slashed checkout to 2 steps with same-day-delivery time-slot booking.",
    result:
      "+32% checkout conversion, +48% average order value, and the team can now publish seasonal collections in under 5 minutes — no developer needed.",
    metrics: [
      { value: "+32%", label: "Conversion" },
      { value: "+48%", label: "Avg Order Value" },
      { value: "1.4s", label: "LCP" },
    ],
  },
  {
    tag: "AUTOMATION",
    title: "Expense Tracker Bot",
    summary:
      "Telegram + n8n pipeline that classifies receipts, syncs to Google Sheets, and emails weekly summaries.",
    image: null,
    accent: "#B026FF",
    code: [
      "$ flowbase deploy expense-bot",
      "→ webhook: telegram.org/bot…",
      "→ n8n workflow ✓ active",
      "→ logged 1,284 expenses",
    ],
    problem:
      "The founders spent ~6 hours/week sorting receipts manually, copying numbers between WhatsApp threads, spreadsheets, and their accountant's portal.",
    solution:
      "We deployed a Telegram bot that ingests photo receipts, runs OCR + LLM classification, and auto-routes structured rows into Google Sheets via a self-healing n8n workflow.",
    result:
      "1,284 expenses logged with 99.2% accuracy in the first quarter and the founders reclaimed an estimated 18 hours/month.",
    metrics: [
      { value: "1,284", label: "Logged" },
      { value: "99.2%", label: "Accuracy" },
      { value: "18h", label: "Saved/mo" },
    ],
  },
  {
    tag: "AI · FINTECH",
    title: "QuantMind AI",
    summary:
      "Real-time financial dashboard surfacing AI-powered alpha signals across 240+ tickers.",
    image:
      "https://images.pexels.com/photos/30268012/pexels-photo-30268012.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    accent: "#22D3EE",
    code: ["model: gpt-5.2", "// signal latency: 18ms"],
    problem:
      "An emerging hedge fund needed a unified live-view of news, sentiment, and macro signals — but their analysts were juggling 9 tabs and missing entry windows.",
    solution:
      "We built QuantMind AI: a streaming dashboard that fuses live tick data with LLM-summarised news and a custom alpha-signal engine, all rendered in a 60fps charting layer.",
    result:
      "Signal latency dropped from minutes to 18ms and the team executed on 3.2× more high-conviction trades in the first month.",
    metrics: [
      { value: "18ms", label: "Signal Latency" },
      { value: "240+", label: "Tickers" },
      { value: "3.2×", label: "Trades / mo" },
    ],
  },
];

export const Portfolio = ({ onConsult }) => {
  const [active, setActive] = useState(null);

  return (
    <>
      <section
        id="work"
        data-testid="portfolio-section"
        className="relative py-24 sm:py-32 overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-[#B026FF] mb-4">
                Selected Work
              </p>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-white">
                <RevealText>Real Impact.</RevealText>{" "}
                <RevealText delay={220} className="neon-text">
                  Proven Results.
                </RevealText>
              </h2>
            </div>
            <p className="text-white/60 max-w-md">
              A glimpse into recent launches — each one shipped, measured, and
              iterated on with our partners.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((p, i) => (
              <ProjectCard
                key={p.title}
                project={p}
                idx={i}
                onOpen={() => setActive(p)}
              />
            ))}
          </div>

          <div className="mt-14 flex justify-center">
            <Magnetic strength={0.3}>
              <Button
                data-testid="view-all-projects-btn"
                data-cursor="hover"
                onClick={() => {
                  const el = document.getElementById("all-projects");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="btn-neon rounded-full h-12 px-7 font-semibold border-0"
              >
                View All Projects
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
            </Magnetic>
          </div>
        </div>
      </section>

      <ProjectModal project={active} onClose={() => setActive(null)} />
    </>
  );
};

const ProjectCard = ({ project, idx, onOpen }) => {
  return (
    <TiltCard
      data-testid={`project-card-${idx}`}
      data-cursor="hover"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="card-base relative rounded-xl overflow-hidden group flex flex-col cursor-pointer"
    >
      {/* Terminal header */}
      <div className="h-10 px-4 border-b border-white/10 flex items-center justify-between bg-white/[0.03]">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
        </div>
        <span className="font-mono-ui text-[10px] uppercase tracking-widest text-white/40">
          ~/{project.title.toLowerCase().replace(/\s+/g, "-")}
        </span>
        <span className="font-mono-ui text-[10px] text-white/30">⌘K</span>
      </div>

      {project.image ? (
        <div className="relative h-48 overflow-hidden">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
        </div>
      ) : (
        <div className="h-48 px-5 py-4 font-mono-ui text-xs text-white/70 flex flex-col gap-1.5 bg-[#05030A]">
          {project.code.map((line, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-[#B026FF]">›</span>
              <span>{line}</span>
            </div>
          ))}
          <div className="flex gap-2 mt-1 items-center">
            <span className="text-[#B026FF]">›</span>
            <span className="w-2 h-4 bg-[#B026FF] animate-pulse" />
          </div>
        </div>
      )}

      <div className="p-6 flex-1 flex flex-col">
        <span
          className="text-[10px] font-mono-ui uppercase tracking-[0.2em] mb-3"
          style={{ color: project.accent }}
        >
          {project.tag}
        </span>
        <h3 className="font-display text-xl font-semibold text-white mb-2">
          {project.title}
        </h3>
        <p className="text-sm text-white/60 leading-relaxed flex-1">
          {project.summary}
        </p>
        <div className="mt-5 pt-5 border-t border-white/5 flex items-center justify-between">
          <span className="text-xs text-white/40 font-mono-ui">
            VIEW CASE STUDY
          </span>
          <ArrowUpRight
            size={16}
            className="text-white/50 group-hover:text-[#B026FF] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition"
          />
        </div>
      </div>
    </TiltCard>
  );
};
